<template>
    <settings-wrapper title="User settings" section="API access">
        <p>Access tokens programmatically authenticate your identity to Nuvolos, allowing applications to perform specific actions.</p>

        <v-skeleton-loader v-if="requestRunning === 'fetchApiKeys'" class="mx-auto" type="table"></v-skeleton-loader>
        <template v-else>
            <div class="d-flex align-center">
                <v-btn @click="openDialog()" class="mr-2" color="primary">
                    <v-icon left>{{ apiKeys.length ? 'mdi-rotate-left' : 'add' }}</v-icon>
                    <span v-if="apiKeys.length">Replace API key</span>
                    <span v-else>API key</span>
                </v-btn>
                <v-spacer></v-spacer>
                <v-text-field
                    placeholder="Search for an API key..."
                    autofocus
                    v-model="search"
                    hide-details
                    prepend-inner-icon="mdi-magnify "
                    solo
                    flat
                    dense
                    clearable
                    class="mr-2"></v-text-field>
                <v-btn :loading="requestRunning === 'fetchApiKeys'" @click="fetchApiKeys()" icon><v-icon>refresh</v-icon></v-btn>
            </div>
            <v-data-table
                :items="apiKeys"
                item-key="apid"
                :headers="headers"
                :search="search"
                :options="tableOptions"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ pagination, options, updateOptions }">
                    <v-data-footer
                        :pagination="pagination"
                        :options="options"
                        @update:options="updateOptions"
                        :itemsPerPageOptions="itemsPerPageOptions"
                        items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                        class="no-border" />
                </template>
                <template v-slot:no-data>
                    <span>No API keys have been added yet. Click add new API key to create a new one.</span>
                </template>
                <template v-slot:[`item.apid`]="{ item }">
                    {{ item.apid }}
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <v-chip color="success" small>{{ item.type }}</v-chip>
                </template>
                <template v-slot:[`item.valid_from`]="{ item }">
                    {{ item.valid_from | dateTimeToHuman }}
                </template>
                <template v-slot:[`item.valid_until`]="{ item }">
                    {{ item.valid_until | dateTimeToHuman }}
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu left offset-x nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <v-list-item @click="openDeleteDialog(item)">
                                <v-list-item-icon>
                                    <v-progress-circular v-if="requestRunning === 'delete'" size="18" width="2" indeterminate color="error" />
                                    <v-icon v-else color="error">mdi-trash-can-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="error--text mr-5">Delete key</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-data-table>
        </template>

        <v-dialog v-model="dialog" max-width="600px" persistent>
            <v-card flat>
                <v-card-title class="flex-nowrap align-start secondary--text">
                    <span v-if="hasGeneratedApiKey">Your new API key</span>
                    <span v-else-if="replacingApiKey">Replace API key</span>
                    <span v-else>Generate API key</span>
                    <v-spacer></v-spacer>
                    <v-btn @click="dialog = false" :disabled="generatingApiKey" aria-label="Close" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <template v-if="hasGeneratedApiKey">
                        <v-alert type="info" text dense class="mt-5 mb-6">
                            <strong>Copy your API key now. You won't be able to view it here again.</strong>
                        </v-alert>
                        <div class="d-flex align-center">
                            <code class="text-body-1 py-2 px-4 mr-2" style="min-width: 0; white-space: normal; word-break: break-all">
                                {{ apiKey }}
                            </code>
                            <copy-to-clipboard :textToCopy="apiKey" tooltip="Copy API key to clipboard" :iconButton="true" />
                        </div>
                    </template>
                    <template v-else>
                        <v-alert v-if="replacingApiKey" type="warning" text dense class="mt-5 mb-0">
                            Generating a new API key immediately invalidates your current key. Applications using it will stop authenticating until updated.
                        </v-alert>
                        <dashboard-error v-if="$isError(apiKey)"></dashboard-error>
                        <dashboard-empty v-else-if="!replacingApiKey">
                            You don't have an API key yet. Generate one to connect your applications to Nuvolos.
                        </dashboard-empty>
                    </template>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn v-if="!hasGeneratedApiKey" @click="addApiKey()" :loading="generatingApiKey" :disabled="generatingApiKey" color="primary" text>
                        <span v-if="replacingApiKey">Replace key</span>
                        <span v-else>Generate key</span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </settings-wrapper>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import eventBus from '@/components/EventBus'
import SettingsWrapper from '@/components/SettingsWrapper.vue'
const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')
const DashboardError = () => import('@/components/DashboardError.vue')
const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')

export default {
    name: 'UserSettingsApiAccess',
    mixins: [enumsData],
    components: {
        SettingsWrapper,
        CopyToClipboard,
        DashboardEmpty,
        DashboardError
    },
    data() {
        return {
            apiKeys: [],
            apiKey: '',
            generatingApiKey: false,
            replacingApiKey: false,
            pendingRevokeApid: null,
            globalDialogHash: `api-key-delete-${this._uid}`,
            search: '',
            dialog: false,
            requestRunning: '',
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            }
        }
    },
    computed: {
        hasGeneratedApiKey() {
            return !!this.apiKey && !this.$isError(this.apiKey)
        },
        headers() {
            return [
                { text: 'Id', align: 'left', value: 'apid' },
                { text: 'Type', align: 'left', value: 'type' },
                { text: 'Last rotation', align: 'left', value: 'valid_from' },
                { text: 'Actions', align: 'right', value: 'actions', sortable: false }
            ]
        }
    },
    methods: {
        openDialog() {
            this.apiKey = ''
            this.replacingApiKey = this.apiKeys.length > 0
            this.dialog = true
        },
        openDeleteDialog(apiKey) {
            this.pendingRevokeApid = apiKey.apid
            this.$store.dispatch('showGlobalDialog', {
                dialogText: `Are you sure you would like to delete the API key?`,
                dialogAction: ['delete', 'cancel'],
                dialogTitle: 'Confirm deletion',
                hash: this.globalDialogHash
            })
        },
        async addApiKey() {
            if (this.generatingApiKey || this.hasGeneratedApiKey) return

            this.generatingApiKey = true
            this.apiKey = ''
            try {
                const { data } = await this.$axios.post(`/authentication/api_key`)
                this.apiKey = data
                await this.fetchApiKeys()
            } catch (error) {
                this.apiKey = error
            } finally {
                this.generatingApiKey = false
            }
        },
        async fetchApiKeys() {
            this.requestRunning = 'fetchApiKeys'
            this.apiKeys = []
            try {
                const { data } = await this.$axios.get(`/authentication/api_key`)
                data.forEach(apiKey => {
                    apiKey.type = 'auto mount'
                })
                this.apiKeys = data
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Error fetching API keys',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.requestRunning = ''
        },
        async deleteApiKey() {
            if (this.pendingRevokeApid === null || this.pendingRevokeApid === undefined) return

            const apid = this.pendingRevokeApid
            this.pendingRevokeApid = null
            try {
                await this.$axios.delete(`/authentication/api_key/${apid}`)
                this.fetchApiKeys()
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Error deleting API key',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
        }
    },
    created() {
        this.fetchApiKeys()
        eventBus.$on(`global-dialog-accept-${this.globalDialogHash}`, this.deleteApiKey)
    },
    beforeDestroy() {
        eventBus.$off(`global-dialog-accept-${this.globalDialogHash}`, this.deleteApiKey)
        this.pendingRevokeApid = null
    }
}
</script>
